import lodash from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { ReactElement } from 'react'

import Field, {
  FieldPrefix,
  Label,
  useField,
} from '../../../../../../../1stlog-ui/src/components/Field'
import useFieldState from '../../../../../../../1stlog-ui/src/components/Field/useFieldState'
import { useFieldArray } from '../../../../../../../1stlog-ui/src/components/FieldArray'
import Icon from '../../../../../../../1stlog-ui/src/components/Icon'
import ToggleSwitch from '../../../../../../../1stlog-ui/src/components/ToggleSwitch'
import { TOrderEntryColiServiceValue } from '../../../../AdditionalServices/types'
import ServiceOption from './ServiceOption'
import { SperrgutLimitation } from './SperrgutLimitation'

const HIDDEN_SERVICES: string[] = []

type TColiServiceProps = {
  fieldName: string
  coliFieldName: string
  readonly?: boolean
}

const ColiService = (props: TColiServiceProps): ReactElement => {
  const { fieldName, coliFieldName, readonly } = props

  const label = useField('label')
  const name = useField('name')
  const [isEnabled, setIsEnabled] = useFieldState<boolean>('isEnabled')
  const handleAutomatically = useField('handleAutomatically')

  const { fields } = useFieldArray<TOrderEntryColiServiceValue[]>('values')
  const [values, setValues] =
    useFieldState<TOrderEntryColiServiceValue[]>('values')

  const isBoolToggle = useMemo(
    () =>
      lodash.size(values) === 1 &&
      lodash.get(lodash.first(values), 'type') === 'bool',
    [values],
  )

  useEffect(() => {
    if (isBoolToggle) {
      setValues(lodash.map(values, (value) => ({ ...value, value: isEnabled })))
    }
  }, [isBoolToggle, isEnabled])

  const disableService = (): void => setIsEnabled(false)

  const hidden =
    (readonly && (!isEnabled || HIDDEN_SERVICES.includes(name))) ||
    (handleAutomatically && !isEnabled)

  return (
    <div className="mb-1" hidden={hidden}>
      <div>
        {readonly ? (
          <div>
            <Icon icon="fas fa-check" className="mr-1" /> <Label>{label}</Label>
          </div>
        ) : (
          <>
            <Field name="isEnabled" noMargin hidden={handleAutomatically}>
              <ToggleSwitch
                label={label}
                id={fieldName}
                disabled={handleAutomatically}
              />
            </Field>
            {handleAutomatically && (
              <div className="ml-4">
                <Label>{`${label} (${i18n.t(
                  'automatically_detected',
                  'automatisch erkannt',
                )})`}</Label>
              </div>
            )}
          </>
        )}
      </div>
      {isEnabled && (
        <div className="ml-4">
          {fields.map((valueName: string) => (
            <FieldPrefix name={valueName} key={valueName} overwritePrefix>
              <ServiceOption
                disableService={disableService}
                serviceName={name}
                coliFieldName={coliFieldName}
                readonly={readonly}
              />
            </FieldPrefix>
          ))}
        </div>
      )}
      <div className="ml-4">
        {name === 'SGZ' && handleAutomatically && (
          <SperrgutLimitation coliFieldName={coliFieldName} />
        )}
      </div>
    </div>
  )
}

export default ColiService
